/* Music and sound, ported in shape from the geomites app's `src/hooks/music.js`.
 *
 * What is carried over, because each part was a bug there first:
 *
 *  - **Volumes are 0-100 and stored**, which is what the settings sliders show, divided down when
 *    handed to the audio layer. One key in `localStorage` beside the language, the shortcuts and
 *    the fog preview — a per-browser setting, like those.
 *  - **A quadratic taper.** Loudness is perceived roughly logarithmically, so a linear map leaves
 *    the low end still too loud; squaring makes 20 quiet and keeps 100 at full.
 *  - **One background track, guarded by a token.** Every request invalidates any older one still
 *    starting, so two quick screen changes can never leave two beds playing at once.
 *  - **Effects re-read the level on every play**, since the slider may have moved since last time.
 *
 * Two things are deliberately different, and both are the web rather than a phone:
 *
 *  - **A browser refuses to make a sound until the player has interacted with the page.** There is
 *    no equivalent on a phone, and it is the whole reason `startMusic` cannot simply play: it
 *    remembers what was asked for and the first real gesture starts it. Ignoring this produces a
 *    console warning and silence for the rest of the session.
 *  - **Almost every effect loads nothing.** geomites caches decoded files so an effect fires in
 *    sync with its animation instead of waiting for a fetch; here an effect is built from
 *    oscillators and a noise buffer at the moment it plays, which has no load to be late for. The
 *    catalogue below was written as the seam where that could change — "an entry becomes a URL,
 *    `playUiSound` grows a branch, and everything above it stays as it is" — and on 2026-09-10 one
 *    did: `scare` is a recording, because a synthesised scream is a synthesiser and the owner
 *    wanted a voice. That is the whole of the branch; every other effect is still oscillators.
 *    Recordings are fetched and decoded once and cached as an `AudioBuffer` (`primeUiSound` warms
 *    one ahead of the moment it has to be in time for), and they go through the same volume and
 *    taper as everything else that sounds.
 */

import backgroundMusicUrl from "src/assets/music/background.mp3"
import zombieGrowlUrl from "src/assets/sounds/zombie_growl.mp3"

const AUDIO_KEY = "hvz-audio"

const clampVolume = (value) => Math.max(0, Math.min(100, Math.round(Number(value) || 0)))
const perceptualGain = (value) => (clampVolume(value) / 100) ** 2

const settings = { music: 55, sfx: 70 }

const read = () => {
  try {
    const raw = window.localStorage.getItem(AUDIO_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (typeof parsed.music === "number") settings.music = clampVolume(parsed.music)
    if (typeof parsed.sfx === "number") settings.sfx = clampVolume(parsed.sfx)
  } catch (error) {
    // A private window, cleared storage, or a hand-edited value: the defaults above are fine.
  }
}
read()

const persist = () => {
  try {
    window.localStorage.setItem(AUDIO_KEY, JSON.stringify(settings))
  } catch (error) {
    // Storage can be full or refused; the session keeps the value in memory either way.
  }
}

export const getMusicVolume = () => settings.music
export const getSfxVolume = () => settings.sfx

/* ---- The audio context ---------------------------------------------------------------------
   Created on demand and never before: constructing one on page load starts it `suspended` and, in
   some browsers, counts against the tab's autoplay reputation. */

let context = null
let musicBus = null

const audioContext = () => {
  if (context) return context
  const Ctor = window.AudioContext || window.webkitAudioContext
  if (!Ctor) return null
  context = new Ctor()
  musicBus = context.createGain()
  musicBus.gain.value = perceptualGain(settings.music)
  musicBus.connect(context.destination)
  return context
}

export const audioIsRunning = () => context?.state === "running"

/* ---- Sound effects -------------------------------------------------------------------------
   A recipe is a list of layers. `tone` is an oscillator sliding from one frequency to another;
   `noise` is a burst of shaped white noise. Everything is short — the longest is 500ms — because
   a UI sound that outlasts the action it reports is a sound a player turns off. */

export const SOUNDS = {
  click: [{ type: "tone", wave: "triangle", from: 660, to: 520, at: 0, length: 0.07, gain: 0.5 }],
  open: [
    { type: "tone", wave: "triangle", from: 440, to: 660, at: 0, length: 0.09, gain: 0.45 },
    { type: "tone", wave: "sine", from: 880, to: 990, at: 0.05, length: 0.09, gain: 0.25 },
  ],
  close: [{ type: "tone", wave: "triangle", from: 560, to: 340, at: 0, length: 0.11, gain: 0.4 }],
  // An order given: a low body with a brushed tail, the sound of a column setting off.
  march: [
    { type: "tone", wave: "sine", from: 180, to: 110, at: 0, length: 0.24, gain: 0.7 },
    { type: "noise", at: 0.02, length: 0.3, gain: 0.16, filter: 1400, sweepTo: 400 },
  ],
  // The player's own turn, handed over.
  endTurn: [
    { type: "tone", wave: "triangle", from: 392, to: 392, at: 0, length: 0.12, gain: 0.45 },
    { type: "tone", wave: "triangle", from: 587, to: 587, at: 0.1, length: 0.22, gain: 0.4 },
  ],
  // Everybody's orders landing at once: heavier, and the only routine sound with a tail.
  round: [
    { type: "tone", wave: "sine", from: 147, to: 147, at: 0, length: 0.9, gain: 0.55 },
    { type: "tone", wave: "sine", from: 294, to: 292, at: 0, length: 0.7, gain: 0.28 },
    { type: "tone", wave: "sine", from: 440, to: 437, at: 0.02, length: 0.5, gain: 0.14 },
  ],
  battle: [
    { type: "noise", at: 0, length: 0.42, gain: 0.4, filter: 2600, sweepTo: 320 },
    { type: "tone", wave: "sawtooth", from: 220, to: 70, at: 0, length: 0.36, gain: 0.35 },
  ],
  // A game starting (requirement 36, Z2): "to arms" — two rising notes a fifth apart, the second
  // held with an octave under it for body. Louder than `open`, which it must not be mistaken for,
  // and under `victory`, whose three notes and tail are the game's loudest fanfare: this is the
  // start of the war, not the end of it. Played once per game by `MatchIntro`, for a seat only.
  start: [
    { type: "tone", wave: "triangle", from: 392, to: 392, at: 0, length: 0.14, gain: 0.46 },
    { type: "tone", wave: "triangle", from: 587, to: 587, at: 0.13, length: 0.34, gain: 0.44 },
    { type: "tone", wave: "sine", from: 294, to: 294, at: 0.13, length: 0.34, gain: 0.22 },
  ],
  /* The round playing itself (requirement 36, M7, Z3). Both quieter than `battle`: a fight is the
     round's loudest thing, and these mark one order landing — the viewer's own strike, or one that
     fell on their side, and the viewer's own tunnel orders. */
  // A missile landing: a short thud that falls away, with a dry crack on the front of it.
  blast: [
    { type: "noise", at: 0, length: 0.08, gain: 0.22, filter: 3200, sweepTo: 900 },
    { type: "tone", wave: "sine", from: 120, to: 45, at: 0.02, length: 0.34, gain: 0.3 },
  ],
  // The tunnel's mouth: a soft swirl, a tone sliding down and back up under brushed air.
  tunnel: [
    { type: "tone", wave: "sine", from: 330, to: 165, at: 0, length: 0.22, gain: 0.18 },
    { type: "tone", wave: "sine", from: 165, to: 300, at: 0.2, length: 0.2, gain: 0.14 },
    { type: "noise", at: 0.04, length: 0.32, gain: 0.08, filter: 1200, sweepTo: 500 },
  ],
  victory: [
    { type: "tone", wave: "triangle", from: 523, to: 523, at: 0, length: 0.14, gain: 0.4 },
    { type: "tone", wave: "triangle", from: 659, to: 659, at: 0.12, length: 0.14, gain: 0.4 },
    { type: "tone", wave: "triangle", from: 784, to: 784, at: 0.24, length: 0.4, gain: 0.42 },
  ],
  defeat: [
    { type: "tone", wave: "triangle", from: 392, to: 392, at: 0, length: 0.16, gain: 0.4 },
    { type: "tone", wave: "triangle", from: 311, to: 311, at: 0.14, length: 0.16, gain: 0.4 },
    { type: "tone", wave: "sine", from: 196, to: 185, at: 0.28, length: 0.5, gain: 0.45 },
  ],
  error: [{ type: "tone", wave: "square", from: 200, to: 150, at: 0, length: 0.16, gain: 0.22 }],
  reaction: [{ type: "tone", wave: "sine", from: 720, to: 1080, at: 0, length: 0.1, gain: 0.3 }],
  // Somebody else typed something (owner, 2026-09-20: "so we are aware"). A soft two-note pop,
  // quieter than a reaction: the chat is a place a player reads when they choose to, and this
  // only has to say that there is something to read. Never played for one's own line.
  chat: [
    { type: "tone", wave: "triangle", from: 880, to: 1046, at: 0, length: 0.06, gain: 0.35 },
    { type: "tone", wave: "sine", from: 1318, to: 1318, at: 0.05, length: 0.08, gain: 0.2 },
  ],
  // A game found (owner, 2026-09-29: the search can run for minutes with the tab in the background,
  // so the room turning up has to be heard). A bright rising three-note chime, longer and higher
  // than `open`/`endTurn` so it cannot be mistaken for either.
  found: [
    { type: "tone", wave: "triangle", from: 659, to: 659, at: 0, length: 0.12, gain: 0.4 },
    { type: "tone", wave: "triangle", from: 880, to: 880, at: 0.11, length: 0.12, gain: 0.4 },
    { type: "tone", wave: "sine", from: 1318, to: 1318, at: 0.22, length: 0.35, gain: 0.35 },
  ],
  /* Every order that was not given a sound of its own before (owner, 2026-10-25: "when upgrading
     building we don't have [one]. Make sure everything (or almost everything) does"). Picked per
     socket action by `orderSounds.js`; nothing else reads these four. */
  // A thing built or set going — a level, a queue, a research, a colony, troops stood down: a
  // dry knock with a low body under it, twice, like a hammer on stone. Not `march`'s brushed tail.
  build: [
    { type: "noise", at: 0, length: 0.05, gain: 0.22, filter: 2600, sweepTo: 900 },
    { type: "tone", wave: "sine", from: 240, to: 170, at: 0, length: 0.12, gain: 0.38 },
    { type: "noise", at: 0.1, length: 0.05, gain: 0.16, filter: 2200, sweepTo: 800 },
    { type: "tone", wave: "sine", from: 300, to: 220, at: 0.1, length: 0.14, gain: 0.3 },
  ],
  // An order taken back: two short steps down, so it cannot be mistaken for `close`'s one slide.
  cancel: [
    { type: "tone", wave: "triangle", from: 494, to: 494, at: 0, length: 0.07, gain: 0.36 },
    { type: "tone", wave: "triangle", from: 370, to: 350, at: 0.06, length: 0.12, gain: 0.34 },
  ],
  // Arriving in a lobby: a low note and a rising one over it, a door opening onto a room.
  enter: [
    { type: "tone", wave: "sine", from: 196, to: 196, at: 0, length: 0.3, gain: 0.4 },
    { type: "tone", wave: "triangle", from: 392, to: 523, at: 0.08, length: 0.2, gain: 0.32 },
  ],
  // Any other press of a button (`bindPressSound`): the quietest thing here, and the shortest.
  press: [{ type: "tone", wave: "triangle", from: 880, to: 700, at: 0, length: 0.045, gain: 0.26 }],
  /* The manual's guide, poked twice (owner, 2026-09-10). **A recording**, and the only effect that
     is one: two goes at synthesising it — a saw tearing upward, then a square shrieking down with a
     detuned twin — were a synthesiser both times, and what the owner wanted was a voice
     ("phatphrogstudio-zombie-voice-growl", handed over as a file).

     **`startAt` skips the file's own silence.** The recording does not open on the growl: it is
     flat until 0.358s, measured by decoding it and walking the samples for the first one over
     0.01. Played from the top, the scare arrives a third of a second after the zombie does — which
     is exactly long enough to stop being a scare. 0.34 is a hair before the onset, so nothing of
     the voice is clipped off the front.

     **And it ignores the effects slider** (owner, 2026-09-10: "100%, no matter the sound effect
     level the user has"). Deliberate, and the only sound in here that does: `alwaysLoud` is one
     flag on one entry rather than a special case in the player, so what is exempt is legible from
     the catalogue. Every other effect, and both beds, still answer to the settings. */
  scare: { url: zombieGrowlUrl, gain: 1, startAt: 0.34, alwaysLoud: true },
}

export const SOUND_KEYS = Object.keys(SOUNDS)

let noiseBuffer = null
const whiteNoise = (ctx) => {
  if (noiseBuffer && noiseBuffer.sampleRate === ctx.sampleRate) return noiseBuffer
  const frames = Math.floor(ctx.sampleRate * 0.5)
  noiseBuffer = ctx.createBuffer(1, frames, ctx.sampleRate)
  const data = noiseBuffer.getChannelData(0)
  for (let i = 0; i < frames; i += 1) data[i] = Math.random() * 2 - 1
  return noiseBuffer
}

/* Every layer gets the same envelope: a 12ms attack so nothing clicks at the start, and an
   exponential fall to silence. `exponentialRampToValueAtTime` never reaches zero, so the floor is
   a hair above it and a hard stop finishes the job. */
const envelope = (ctx, gain, level, start, length) => {
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(Math.max(level, 0.0002), start + Math.min(0.012, length / 3))
  gain.gain.exponentialRampToValueAtTime(0.0001, start + length)
}

/* Decoded recordings, by url. A promise is cached rather than the buffer, so two presses a frame
   apart share one fetch, and a failure caches `null` rather than retrying on every press — a sound
   that cannot load is a sound this page does without. */
const recordings = new Map()

const loadRecording = (ctx, url) => {
  if (recordings.has(url)) return recordings.get(url)
  let pending
  try {
    pending = window
      .fetch(url)
      .then((response) => response.arrayBuffer())
      .then((bytes) => ctx.decodeAudioData(bytes))
      .catch(() => null)
  } catch (error) {
    // No fetch, or a context with no decoder: jsdom, and any browser too old for either.
    pending = Promise.resolve(null)
  }
  recordings.set(url, pending)
  return pending
}

/* Fetch and decode ahead of time, for a sound that has to land on a frame rather than whenever it
   is ready. The manual's guide calls this on the first poke, so the scare on the second is in the
   buffer before it is asked for. Safe to call as often as you like. */
export const primeUiSound = (key) => {
  const recipe = SOUNDS[key]
  if (!recipe || !recipe.url) return
  const ctx = audioContext()
  if (ctx) loadRecording(ctx, recipe.url)
}

const playRecording = (ctx, recipe, level) => {
  Promise.resolve(loadRecording(ctx, recipe.url)).then((buffer) => {
    if (!buffer) return
    const gain = ctx.createGain()
    gain.gain.value = (recipe.gain === undefined ? 1 : recipe.gain) * level
    gain.connect(ctx.destination)
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(gain)
    // `start(when, offset)`: a recording that opens on silence is played from past it.
    source.start(0, Math.min(recipe.startAt || 0, buffer.duration))
  })
}

/* How long an effect waits for a context it asked to resume. Inside a gesture, and on a page the
   browser trusts, `resume()` settles within a few milliseconds; one that is refused never does, or
   does only when a later gesture lets it — and the round sound of two minutes ago is not something
   to play then. A quarter of a second is the whole budget. */
const RESUME_GRACE_MS = 250

/* When the last effect was asked for, and which. `bindPressSound` reads it to stay quiet for a press
   that already made a sound of its own; `playUiSound(key, { unlessWithinMs })` reads it so two
   doors to one moment (a game found, then the lobby it opens) do not both sound. */
let lastSound = { key: null, at: -Infinity }
const now = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now())
export const msSinceLastSound = () => now() - lastSound.at
export const lastUiSound = () => lastSound.key

export const playUiSound = (key, { unlessWithinMs = 0 } = {}) => {
  const recipe = SOUNDS[key]
  if (!recipe) return
  if (unlessWithinMs && msSinceLastSound() < unlessWithinMs) return
  lastSound = { key, at: now() }
  // `alwaysLoud` is the owner's call on one sound (see `scare`) and nothing else reads the flag:
  // it plays at full gain whatever the slider says, silenced sliders included.
  const level = recipe.alwaysLoud ? 1 : perceptualGain(settings.sfx)
  if (level <= 0) return
  const ctx = audioContext()
  if (!ctx) return
  if (ctx.state === "running") {
    sound(ctx, recipe, level)
    return
  }
  /* Not running. Hidden, it stays quiet — that is the point of `silenceForBackground`. Before any
     gesture, there is nothing to be done: a `resume()` outside one is refused with a console
     warning, and the first tap resumes the context anyway (`unlockAudio`). Otherwise the context
     is asked to resume and the effect plays once it has — which is the ordinary case for the
     effect of the very tap that resumed it: `pointerdown` issued the `resume()`, the state flips
     a moment later, and the `click` asking for its sound used to find it still `suspended` and
     give up. Every effect after a return from the background was lost that way (requirement 36,
     Z1). The `resume()` is issued synchronously, which is what WebKit needs of a call made inside
     a gesture; the nodes themselves may be built afterwards. */
  if (backgrounded || !unlocked || !ctx.resume) return
  const asked = Date.now()
  Promise.resolve(ctx.resume()).then(
    () => {
      if (backgrounded || ctx.state !== "running" || Date.now() - asked > RESUME_GRACE_MS) return
      sound(ctx, recipe, level)
    },
    () => {}
  )
}

const sound = (ctx, recipe, level) => {
  if (recipe.url) {
    playRecording(ctx, recipe, level)
    return
  }

  const now = ctx.currentTime
  recipe.forEach((layer) => {
    const start = now + layer.at
    const gain = ctx.createGain()
    envelope(ctx, gain, layer.gain * level, start, layer.length)
    gain.connect(ctx.destination)

    if (layer.type === "noise") {
      const source = ctx.createBufferSource()
      source.buffer = whiteNoise(ctx)
      const filter = ctx.createBiquadFilter()
      filter.type = "lowpass"
      filter.frequency.setValueAtTime(layer.filter, start)
      if (layer.sweepTo) filter.frequency.exponentialRampToValueAtTime(layer.sweepTo, start + layer.length)
      source.connect(filter)
      filter.connect(gain)
      source.start(start)
      source.stop(start + layer.length)
      return
    }

    const osc = ctx.createOscillator()
    osc.type = layer.wave
    osc.frequency.setValueAtTime(layer.from, start)
    if (layer.to !== layer.from) osc.frequency.exponentialRampToValueAtTime(layer.to, start + layer.length)
    osc.connect(gain)
    osc.start(start)
    osc.stop(start + layer.length)
  })
}

/* ---- Music ----------------------------------------------------------------------------------
   One recording — `assets/music/background.mp3` ("Hypnotic Dread") — looped as the bed everywhere.
   It replaced two synthesized oscillator pads; the two track names survive because the callers
   (App mounts `main`, `useMusic` asks for `war` on the map) still speak them. Both point at the
   same file today, and a track change onto the same file relabels the bed instead of restarting
   it, so walking into a game does not restart the music. A second recording is one entry here. */

const TRACKS = {
  main: { url: backgroundMusicUrl },
  war: { url: backgroundMusicUrl },
}

export const TRACK_KEYS = Object.keys(TRACKS)

/* Every background request bumps this. A track that finishes starting and finds the number has
   moved on tears itself down instead of playing over its successor. */
let musicToken = 0
let playing = null
let wanted = null

const stopVoices = (node) => {
  if (node.media) {
    try {
      node.media.pause()
    } catch (error) {
      // jsdom has no play/pause; a bed that cannot pause is one that never sounded.
    }
  }
  try {
    node.out.disconnect()
  } catch (error) {
    // Already disconnected.
  }
}

const buildTrack = (ctx, spec, onRefused) => {
  const out = ctx.createGain()
  out.gain.value = 0
  out.connect(musicBus)
  // Eight seconds to come up: a bed that arrives should not announce itself.
  out.gain.setValueAtTime(0.0001, ctx.currentTime)
  out.gain.exponentialRampToValueAtTime(1, ctx.currentTime + 8)

  // A media element rather than fetch-and-decode: the browser streams the file instead of holding
  // a decoded copy of the whole track in memory, and routing it through the graph keeps the volume
  // slider and its taper in charge, same as everything else that sounds.
  const media = new window.Audio(spec.url)
  media.loop = true
  const source = ctx.createMediaElementSource(media)
  source.connect(out)
  /* `media.play()` has to be called here and not a microtask later: WebKit only honours it while
     the gesture that led to it is still live (see `unlockAudio`). */
  const attempt = (() => {
    try {
      return media.play()
    } catch (error) {
      return null // jsdom throws instead of returning a promise; there is nothing to hear there.
    }
  })()
  /* A refusal arrives as a rejected promise rather than as an exception, and swallowing it was
     silence for the rest of the session: `playing` stayed set to a bed making no sound, so
     `musicIsPlaying()` said yes, `startWanted` found the track it was asked for already playing
     and returned, and no later gesture ever tried again. */
  if (attempt && attempt.catch) attempt.catch(() => onRefused && onRefused())

  return { out, media, source }
}

const startWanted = () => {
  const ctx = audioContext()
  if (!ctx) return
  /* `ctx.state` is still "suspended" for a moment after `resume()` is called, and that moment is
     the only one in which the bed may start: WebKit honours a `play()` issued inside the gesture
     and refuses the same call one microtask later. So once a gesture has been seen we go ahead —
     `unlocked` is the flag — rather than waiting for the state to catch up. A context that is
     already running needs no gesture; that is every browser after the first tap, and jsdom. */
  if (!unlocked && ctx.state !== "running") return
  // Nothing starts while the app is in the background; coming back starts what is wanted.
  if (backgrounded) return
  if (!wanted || !TRACKS[wanted]) return
  if (playing && playing.track === wanted) return
  if (playing && TRACKS[playing.track].url === TRACKS[wanted].url) {
    // Same recording under a different name: relabel, so a screen change never restarts the music.
    playing.track = wanted
    return
  }
  musicToken += 1
  const token = musicToken
  if (playing) {
    stopVoices(playing.node)
    playing = null
  }
  if (perceptualGain(settings.music) <= 0) return
  const track = wanted
  const node = buildTrack(ctx, TRACKS[track], () => {
    // Refused. Put it back the way it was, so the next gesture is a fresh attempt and not a no-op.
    if (musicToken !== token) return
    stopVoices(node)
    playing = null
    wanted = track
  })
  playing = { track, node }
}

/* Ask for a bed. It starts now if the browser has let us make a sound yet, and otherwise the
   moment the player first touches the page — see `unlockAudio`. */
export const startMusic = (track) => {
  wanted = track
  startWanted()
}

export const stopMusic = () => {
  wanted = null
  musicToken += 1
  if (playing) {
    stopVoices(playing.node)
    playing = null
  }
}

export const musicIsPlaying = () => Boolean(playing)

export const setMusicVolume = (value) => {
  settings.music = clampVolume(value)
  persist()
  const level = perceptualGain(settings.music)
  if (musicBus && context) {
    // Live, so the change is audible while the settings pop-up is open.
    musicBus.gain.setTargetAtTime(level, context.currentTime, 0.05)
  }
  // Zero tears the bed down rather than muting it: an oscillator nobody can hear is still a
  // timer and a graph, and a player who turned the music off meant off.
  if (level <= 0 && playing) {
    const keep = playing.track
    stopVoices(playing.node)
    playing = null
    wanted = keep
  } else if (level > 0) {
    startWanted()
  }
}

export const setSfxVolume = (value) => {
  // Effects read the level when they play, so persisting is the whole of it.
  settings.sfx = clampVolume(value)
  persist()
}

/* ---- Autoplay -------------------------------------------------------------------------------
   Chrome, Safari and Firefox all refuse to start audio until the page has been interacted with,
   and a context created before that opens `suspended`. So the first real gesture resumes it and
   starts whatever was asked for in the meantime.

   **Everything that has to happen inside the gesture happens inside the gesture**, and that is the
   whole of this section (owner, 2026-09-12: no music on an iPhone). This used to resume the
   context and start the bed in the resume promise's callback — one microtask after the tap. Chrome
   and Firefox accept that: they hand the page a sticky activation that outlives the handler, so a
   `play()` any time afterwards is fine. WebKit does not. A media element may only start while the
   gesture that led to it is still being dispatched, so on an iPhone every attempt was refused, the
   rejection was swallowed, and the game played in silence — in Safari's tab and installed alike,
   which is why no amount of tapping brought it back.

   Two things follow, and the second is what makes the first survivable:

   - `resume()` and the `play()` are both issued synchronously from the handler. The resume
     promise is still listened to, as the retry for a browser whose context was genuinely not
     ready; it is no longer what the music waits for.
   - **The listeners stay until something has actually sounded.** A one-shot binding spends the
     session's only chance on a tap WebKit may not count (one that ends a scroll, one on a passive
     touchstart), and a refusal is reported asynchronously, so the handler cannot know in time
     whether it worked. It checks at the top of the *next* gesture instead and unbinds there.

   And one more, found when the effects went silent in a game (requirement 36, Z1, 2026-10-23):
   **a gesture is recorded whether or not it had anything to unlock.** Chrome creates the context
   already `running` on a site whose media the player has let play before — every regular player —
   so the first tap found nothing to do and the listener took itself off without `unlocked` ever
   being set. The tab was hidden and shown again, `resumeFromBackground` saw no gesture and resumed
   nothing, and every effect not fired from a tap (the round, the verdict, a reaction) was silent
   until the next press. `unlocked` means "this page has been touched", nothing more. */

let unlockBound = false
let unlocked = false
let backgrounded = false
// Whether `silenceForBackground` is what suspended the context: a context that was running when the
// page was hidden had leave to run, so it is resumed on return whether a gesture was seen or not.
let suspendedByUs = false

// Nothing left to start: no bed was asked for, one is playing (and its element is not paused —
// a restart the browser refused leaves it built and silent), or the player turned the music off.
const musicIsSettled = () =>
  !wanted || (playing !== null && !playing.node?.media?.paused) || perceptualGain(settings.music) <= 0

export const unlockAudio = () => {
  const ctx = audioContext()
  if (!ctx) return
  unlocked = true
  // Anything but running: "suspended", and WebKit's "interrupted" after a call or the background,
  // which it does not always end by itself — a bed resumed into an interrupted graph is silent.
  if (ctx.state !== "running" && ctx.resume) ctx.resume().then(startWanted, () => {})
  startWanted()
  /* A bed that is already built but paused — iOS pauses the element when the app goes to the
     background — is restarted rather than rebuilt: `startWanted` sees the track it wants already
     playing and would return. */
  const media = playing?.node?.media
  if (media && media.paused) {
    try {
      const attempt = media.play()
      if (attempt && attempt.catch) attempt.catch(() => {})
    } catch (error) {
      // jsdom, or an element the browser will not restart; the next gesture comes back here.
    }
  }
}

/* A press of any button that made no sound of its own gets `press` (owner, 2026-10-25). Wired once,
   on the document, rather than in each of the hundreds of buttons: the capture listener notes the
   press before React's handlers run, and a moment later — after the handler and any effect it
   flushed — sounds `press` only if nothing else sounded since. So a press that opened a modal, gave
   an order or ended the turn keeps its own sound and gets no click on top. The map is a canvas, not
   a button, so a cell pressed stays silent; a `data-silent` element (and anything inside it) does
   too. */
const PRESSABLE = 'button, [role="button"], [role="tab"], a[href], input[type="checkbox"], input[type="radio"], summary'
const PRESS_SETTLE_MS = 30

export const bindPressSound = () => {
  if (typeof document === "undefined") return () => {}
  const onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest(PRESSABLE) : null
    if (!target || target.disabled || target.closest("[data-silent]")) return
    const pressedAt = now()
    window.setTimeout(() => {
      if (lastSound.at >= pressedAt) return
      playUiSound("press")
    }, PRESS_SETTLE_MS)
  }
  document.addEventListener("click", onClick, true)
  return () => document.removeEventListener("click", onClick, true)
}

export const bindAudioUnlock = () => {
  if (unlockBound || typeof window === "undefined") return () => {}
  unlockBound = true
  const events = ["pointerdown", "touchend", "click", "keydown"]
  const off = () => {
    events.forEach((name) => window.removeEventListener(name, onGesture))
    unlockBound = false
  }
  function onGesture() {
    // Seen, whatever state the context is in: this is what lets the page resume it later.
    unlocked = true
    if (audioIsRunning() && musicIsSettled()) {
      off()
      return
    }
    unlockAudio()
  }
  events.forEach((name) => window.addEventListener(name, onGesture, { passive: true }))
  return off
}

/* ---- Leaving the app ------------------------------------------------------------------------
   **The game goes quiet the moment it is not on screen** (owner, 2026-10-02: *"It's not spotify,
   it's a game"*). A media element keeps playing in a background tab and in an installed app sent
   to the home screen, and Android then hangs a media notification on the lock screen for it.

   Hidden (`visibilitychange`, and `pagehide` for a page going into the back/forward cache): the
   bed's element is paused and the context suspended, which cuts any effect still ringing and
   refuses new ones (`playUiSound` builds nothing while `backgrounded`). The Media Session is
   cleared so no lock-screen player is left behind offering to start the music again.

   Shown again: the context resumes and the bed that was playing picks up where it was — nothing
   if the player had turned the music off or no bed was wanted. Coming back is not a gesture, but
   a context that has sounded once may resume without one, and iOS needs it to: it suspends the
   context and pauses the element on its own whenever the app goes to the background, and brings
   neither back. This listener stays for the whole session; the gesture one above unbinds. */

const clearMediaSession = () => {
  const session = typeof navigator !== "undefined" ? navigator.mediaSession : null
  if (!session) return
  try {
    session.metadata = null
    session.playbackState = "none"
  } catch (error) {
    // A browser with a partial Media Session; there is nothing on its lock screen to clear.
  }
}

export const silenceForBackground = () => {
  backgrounded = true
  const media = playing?.node?.media
  if (media && !media.paused) {
    try {
      media.pause()
    } catch (error) {
      // jsdom, or an element already gone.
    }
  }
  if (context && context.state === "running" && context.suspend) {
    suspendedByUs = true
    Promise.resolve(context.suspend()).catch(() => {})
  }
  clearMediaSession()
}

export const resumeFromBackground = () => {
  if (!backgrounded) return
  backgrounded = false
  // A page that never had its first gesture keeps waiting for one, as it did before it was hidden
  // — unless the context was running when it went, in which case it had leave to and gets it back.
  if (unlocked || suspendedByUs) unlockAudio()
  suspendedByUs = false
  // And a restart the browser refuses with no gesture in hand (iOS, back from the background) is
  // silent for the session unless the next tap tries again: the gesture listener unbound itself
  // once the bed first sounded, so it is bound again here. A no-op while it is still bound.
  bindAudioUnlock()
}

export const bindBackgroundSilence = () => {
  if (typeof document === "undefined") return () => {}
  const onVisibility = () => {
    if (document.visibilityState === "hidden") silenceForBackground()
    else resumeFromBackground()
  }
  const onPageHide = () => silenceForBackground()
  const onPageShow = () => {
    if (document.visibilityState !== "hidden") resumeFromBackground()
  }
  document.addEventListener("visibilitychange", onVisibility)
  window.addEventListener("pagehide", onPageHide)
  window.addEventListener("pageshow", onPageShow)
  return () => {
    document.removeEventListener("visibilitychange", onVisibility)
    window.removeEventListener("pagehide", onPageHide)
    window.removeEventListener("pageshow", onPageShow)
  }
}

// Tests mount and unmount; without this the module-level state leaks between them.
export const resetAudioForTests = () => {
  musicToken += 1
  playing = null
  wanted = null
  context = null
  musicBus = null
  noiseBuffer = null
  unlockBound = false
  unlocked = false
  backgrounded = false
  suspendedByUs = false
  lastSound = { key: null, at: -Infinity }
  settings.music = 55
  settings.sfx = 70
}

export { perceptualGain, AUDIO_KEY }
